import { useState } from 'react';
import { Button, Typography, Grid, FormControl, Select  } from '@material-ui/core';
import ContentWrapper from 'components/ContentWrapper';
import makePost from 'helpers/makePost';
import { useDispatch } from 'store';
import { addFeedback } from 'actions';
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { QuoteFields, quoteDefaultValues, quoteFormValidation, salesRepInitials } from '../../components/QuoteFields';
import Header from '../Shared/Header';
import * as Yup from 'yup';
import ReactHookTextField from '../../../../components/ReactHookTextField';
import { pdf } from '@react-pdf/renderer';
import PDF from '../Shared/PDF';
import { saveAs } from 'file-saver';
import { axiosInstance } from 'App';
import { useStyles } from '../../styles';
import Files from 'components/Files';
import CreateJob from 'components/CreateJob';
import { useAuth } from 'providers/auth';
import { FanComps } from '../Shared/FanComps';

export const formValidation = Yup.object().shape({
    ...quoteFormValidation.fields,
    lineItems: Yup.array().of(Yup.object().shape({
        quantity: Yup.number().transform(value => (isNaN(value) ? undefined : value)).required('Qty is required'),
        price: Yup.number().transform(value => (isNaN(value) ? undefined : parseFloat(value.toFixed(2)))),
    })),
    attributes: Yup.object().shape({
        fanSellPrice: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanEstimatedCost: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanQty: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        impellerDiameter: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanVolume: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanInletPressure: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanDischargePressure: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanRpm: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanDesignTempC: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        fanDutyKW: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        motorKW: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
        motorSpeed: Yup.number().transform(value => (isNaN(value) ? undefined : value)),
    })
});

const Form = ({ quoteData, quoteDetails, itemList, revList, clientList, quoteStatusList, salesRepList, productCategoryList, productAreaList, enquirySourceList, industryList, applicationList }) => {
    const dispatch = useDispatch()
    const { userAttr } = useAuth();
    const classes = useStyles();
    const { quoteID, itemID, revision } = quoteData;
    const { attributes, lineItems, validity } = quoteDetails;

    const [totalPrice, setTotalPrice] = useState(lineItems.map(e => e.quantity * e.price));
    
    const calcTotalPrice = () => totalPrice.reduce((a, b) => {   
        return (a + b);
    }, 0);

    const { handleSubmit, watch, control, setError, setValue, getValues } = useForm({
        resolver: yupResolver(formValidation),
        defaultValues: {
            ...quoteDefaultValues(quoteData),
            attributes: {
                ...attributes,
                deliveryTerm: attributes && attributes.deliveryTerm !== null ? attributes.deliveryTerm : "Ex. Works Melbourne"
            },
            lineItems: lineItems.length ? lineItems : [],
            validity: validity !== null ? validity : "30 Days Nett",
            updatedAt: quoteDetails.updatedAt
        } 
      });

    const onSubmit = async data => {
        try {
            const response = await makePost({
                url: `/quotedetails/${quoteID}/${itemID}/${revision}/manualentryfans`,
                data: { 
                    ...data,
                    totalPrice: calcTotalPrice(),
                    combinedPrice: calcTotalPrice() + (data.attributes?.fanQty * data.attributes?.fanSellPrice || 0),
                    combinedCost: (data.attributes?.fanQty * data.attributes?.fanEstimatedCost || 0)
                },
            });

            if (response.status === 200) {
                setValue('updatedAt', response.data.updatedAt)
            }

            dispatch(addFeedback('Quote saved', 'success'));
        } catch (error) {
            const errData = error.response.data;

            if (errData === "Stale update. Reload and update again") {
                dispatch(addFeedback(errData, 'error'));
            } else {
                Object.keys(errData.errors).map((key) => {
                    setError(key.slice(2), errData.errors[key][0]);
                    return null;
                });
    
                dispatch(addFeedback(errData.title || errData, 'error', errData.errors));
            }
        }
    };


    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Header quoteData={quoteData} itemList={itemList} revList={revList} productCategoryList={productCategoryList} getValues={getValues} />

            <ContentWrapper>
                <QuoteFields
                    control={control}
                    clientList={clientList}
                    quoteStatusList={quoteStatusList}
                    salesRepList={salesRepList}
                    productAreaList={productAreaList}
                    enquirySourceList={enquirySourceList}
                    industryList={industryList}
                    applicationList={applicationList}
                    watch={watch} 
                    setValue={setValue} />
            </ContentWrapper>
            
            <Typography variant="h2">
                Manual Entry Fan
            </Typography>

           <FanComps control={control} totalPrice={totalPrice} setTotalPrice={setTotalPrice} calcTotalPrice={calcTotalPrice} />
            
            <ContentWrapper>

                <ReactHookTextField
                    name='validity'
                    type='text'
                    control={control}
                    label='Validity'
                    style={{width: '19%', marginRight: '1%'}}
                />

                <ReactHookTextField
                    name='attributes.leadTime'
                    type='text'
                    control={control}
                    label='Lead Time'
                    style={{width: '19%', marginRight: '1%'}}
                />

                <ReactHookTextField
                    name='attributes.deliveryTerm'
                    type='text'
                    control={control}
                    label='Delivery Term'
                    style={{width: '19%', marginRight: '1%'}}
                />

                <ReactHookTextField
                    name='attributes.extraText'
                    type='textarea'
                    control={control}
                    label='Extra Text'
                    style={{width: '100%'}}
                    InputProps={{
                        multiline:true
                    }}
                />
            </ContentWrapper>

            <Grid container justifyContent="space-between">
                <Grid>
                    { userAttr.roles.includes("Admin") && <>
                        <Typography variant="h1" className={classes.header} style={{ display: 'inline' }}>State: </Typography>
                        <Controller
                            name='quoteStateID'
                            type={'select'}
                            control={control}
                            render={({ field, fieldState: { error } }) =>
                                <FormControl variant='outlined' className={classes.select}>
                                    <Select
                                        native
                                        {...field}
                                        margin='dense'
                                        onChange={(e) => {
                                            return field.onChange(e.target.value)
                                        }}>
                                            <option key='state-1' value='1'>Open</option>
                                            <option key='state-2' value='2'>Archive</option>
                                    </Select>
                                </FormControl>
                            }
                        />
                    </>}
                </Grid>
                <Grid>
                    <Button
                        variant='contained'
                        color='secondary'
                        type='submit'
                    >
                        Save Quote 
                    </Button>
                    &nbsp;
                    <Button
                        variant='contained'
                        color='primary'
                        type='submit'
                        onClick={async () => {
                                const quoteRes = await axiosInstance({ url: `/quotes/${quoteID}/${itemID}/${revision}` });
                                const quoteDetailsRes = await axiosInstance({ url: `/quotedetails/${quoteID}/${itemID}/${revision}/customfans` });
                                const quoteData = quoteRes.data;
                                
                                const blob = await pdf((
                                    <PDF quoteData={quoteData} quoteDetails={quoteDetailsRes.data}/>
                                )).toBlob();
                                
                                const initials = quoteData.salesRepName ? salesRepInitials(quoteData.salesRepName) : 'NoSalesRep';
                                    
                                saveAs(blob, `${quoteID}${revision}${itemID}${initials}.pdf`);
                        }}
                    >
                        Generate PDF 
                    </Button>
                    &nbsp;
                    <CreateJob url={`/jobs/new/${quoteID}/${revision}/${itemID}`} />
                </Grid>
            </Grid>
            <br />

            <Typography variant="h2">
                Files
            </Typography>

            <ContentWrapper style={{display: 'flex'}}>
                <Files url={`/quotes/${quoteID}/${itemID}/${revision}/files`} />
            </ContentWrapper>
        </form>
    );
};

export default Form;
